import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import classNames from 'classnames'
import { Line } from 'react-chartjs-2'

import { withStyles } from '@material-ui/core/styles'

class LineChartView extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    label: PropTypes.string.isRequired,
    dataMin: PropTypes.array,
    dataMax: PropTypes.array,
    formatter: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    lower: PropTypes.number,
    upper: PropTypes.number,
    size: PropTypes.string,
    timeMax: PropTypes.instanceOf(moment),
    timeMin: PropTypes.instanceOf(moment),
  }

  static defaultProps = {
    formatter: (v) => v,
    dataMin: [],
    dataMax: [],
    min: null,
    max: null,
    lower: null,
    upper: null,
    size: null,
    timeMax: null,
    timeMin: null,
  }

  static styles = (theme) => ({
    chart: {
      position: 'relative',
      minHeight: 200,
      marginBottom: theme.spacing(5),
    },
    full_height: {
      height: 'calc(100vh - 200px)', // calculate the height based on the viewport height
    },
    half_height: {
      height: 'calc(70vh - 200px)',
    },
  })

  defaultConfig = () => ({
    pointRadius: 0,
    pointHoverRadius: 3,
    lineTension: 0,
    borderWidth: 3,
    borderColor: 'rgba(0, 0, 255, 0.5)',
    fill: false,
  })

  noShowConfig = () => ({
    pointRadius: 0,
    pointHoverRadius: 0,
    lineTension: 0,
    borderWidth: 0,
  })

  configMinMax() {
    const { min, max } = this.props
    const config = {}
    if (min != null) {
      config.suggestedMin = min
    }
    if (max != null) {
      config.suggestedMax = max
    }
    return config
  }

  configTimerange() {
    const { timeMax, timeMin } = this.props
    const config = {}
    if (timeMax != null) {
      config.max = timeMax
    }
    if (timeMin != null) {
      config.min = timeMin
    }
    return config
  }

  options() {
    return {
      maintainAspectRatio: false,
      scales: {
        xAxes: [{
          type: 'time',
          ticks: {
            major: {
              enabled: true,
            },
          },
          time: {
            ...this.configTimerange(),
            minUnit: 'second',
            displayFormats: {
              // This is used to calculate how many ticks to show, so force it to be extra long.
              millisecond: 'h:mm:ss.SSS a            ',
              hour: 'h:mm a',
              month: 'll',
            },
          },
        }],
        yAxes: [{
          offset: true,
          ticks: {
            callback: this.props.formatter,
            ...this.configMinMax(),
          },
        }],
      },
      legend: {
        labels: {
          filter: (legendItem) => legendItem.datasetIndex === 0,
        },
      },
      tooltips: {
        filter: (tooltipItem) => tooltipItem.datasetIndex === 0,
        callbacks: {
          label: (tooltipItem, data) => {
            // Copied from https://github.com/chartjs/Chart.js/blob/master/src/core/core.tooltip.js#L65
            let label = data.datasets[tooltipItem.datasetIndex].label || ''

            if (label) {
              label += ': '
            }
            label += this.props.formatter(tooltipItem.yLabel)
            return label
          },
        },
      },
    }
  }

  displayLowerBand() {
    return this.props.lower != null
  }

  displayUpperBand() {
    return this.props.upper != null
  }

  displayNormalBand() {
    return this.displayLowerBand() || this.displayUpperBand()
  }

  normalBand() {
    const { data, lower, upper } = this.props
    const normalBandColor = 'rgba(0, 255, 0, 0.1)'
    const bands = []
    const bandOptions = {
      ...this.noShowConfig(),
      borderColor: normalBandColor,
      backgroundColor: normalBandColor,
    }
    if (data && data.length > 1) {
      const start = data[0].x
      const end = data[data.length - 1].x
      if (this.displayLowerBand()) {
        bands.push({
          ...bandOptions,
          data: [
            { x: start, y: lower },
            { x: end, y: lower },
          ],
          fill: this.displayUpperBand() ? '+1' : 'end',
          label: 'Lower', // Without label, redrawing gets funky
        })
      }
      if (this.displayUpperBand()) {
        bands.push({
          ...bandOptions,
          data: [
            { x: start, y: upper },
            { x: end, y: upper },
          ],
          fill: this.displayLowerBand() ? false : 'start',
          label: 'Upper', // Without label, redrawing gets funky
        })
      }
    }
    return bands
  }

  render() {
    const {
      classes, data, dataMin, dataMax, label, size,
    } = this.props

    let clNames = classes.chart
    switch (size) {
      case 'half_height':
        clNames = classNames(clNames, classes.half_height)
        break
      default:
        clNames = classNames(clNames, classes.full_height)
    }

    const datasets = [
      {
        ...this.defaultConfig(),
        label,
        data,
      },
      {
        ...this.noShowConfig(),
        label: 'Minimum',
        borderColor: 'rgba(0, 0, 255, 0.1)',
        backgroundColor: 'rgba(0, 0, 255, 0.1)',
        fill: '+1',
        data: dataMin,
      },
      {
        ...this.noShowConfig(),
        label: 'Maximum',
        borderColor: 'rgba(0, 0, 255, 0.1)',
        fill: false,
        data: dataMax,
      },
      ...this.normalBand(),
    ]
    const chart = { datasets }

    return (
      <div className={clNames}>
        <Line data={chart} options={this.options()} />
      </div>
    )
  }
}

export default withStyles(LineChartView.styles)(LineChartView)
